/**
 * Product analytics, off unless configured.
 *
 * PostHog answers the questions the server cannot: which screens people open
 * without asking us for anything, where they give up, and what they did before
 * they left. It is a second home for the same events the portal already sends
 * to Grafana Faro - the vocabulary is shared, so a funnel here and a panel
 * there describe the same thing.
 *
 * Nothing leaves the browser until VUE_APP_RC_POSTHOG_KEY is set, and never in
 * development. With it off, the SDK is not even downloaded: it lives in its own
 * webpack chunk behind the dynamic import below.
 *
 * Pages do not talk to this file. They use the useRum() composable, which sends
 * to both sinks.
 */
import { scrubUrl } from './scrubUrl.js';
import { dropNoisyEvent } from '@/plugins/telemetryNoise';

/** EU cloud. The project lives in the EU region, so the default host matches. */
const DEFAULT_HOST = 'https://eu.i.posthog.com';

let posthogPromise = null;
let posthog = null;

const config = () => {
  return {
    key: process.env.VUE_APP_RC_POSTHOG_KEY,
    host: process.env.VUE_APP_RC_POSTHOG_HOST || DEFAULT_HOST,
    version: process.env.VUE_APP_RC_VERSION,
    api: process.env.VUE_APP_RC_API,
    build: process.env.NODE_ENV,
  };
};

/**
 * Which deployment this browser is talking to, taken from the API host so it
 * cannot disagree with where the data actually goes.
 */
const environment = (api) => {
  if (!api) {
    return 'unknown';
  }
  if (api.includes('localhost')) {
    return 'development';
  }
  if (api.includes('readychek.cloud')) {
    return 'cloud';
  }
  return 'production';
};

/**
 * Analytics is off in development for the same reasons monitoring is: a
 * developer reloading a page all afternoon is not a fleet manager, and would
 * both cost money and make the real numbers meaningless. Three locks, matching
 * plugins/rum.js: no key, a development build, or a localhost API.
 */
const isEnabled = () => {
  const values = config();
  if (!values.key) {
    return false;
  }
  if (values.build === 'development') {
    return false;
  }
  return environment(values.api) !== 'development';
};

/**
 * Everything PostHog is about to send passes through here.
 *
 * Portal URLs carry encoded record ids, and PostHog puts the current URL on
 * every event, so each one is scrubbed the same way Faro's are.
 *
 * Knowing which town a driver was in is not worth anything to us, and stopping
 * it has two false starts worth recording. Deleting `$ip` does nothing, because
 * PostHog reads the address off the request itself, server side. Nor does the
 * project's `anonymize_ips`, which is already on: the address is geolocated
 * before it is dropped. `$geoip_disable` on the event is what the ingest
 * actually honours, so it is set here, on the one path every event takes.
 *
 * There is no `disable_geoip` init option in posthog-js 1.434, whatever the
 * shape of the other `disable_*` settings suggests. Passing one is accepted in
 * silence and changes nothing - which is how every event between 2026-09-20 and
 * 2026-09-21 still arrived carrying a city.
 */
const sanitize = (properties) => {
  try {
    const clean = { ...properties };
    ['$current_url', '$referrer', '$pathname', '$initial_current_url', '$initial_referrer'].forEach((key) => {
      if (clean[key]) {
        clean[key] = scrubUrl(clean[key]);
      }
    });
    delete clean.$ip;
    clean.$geoip_disable = true;
    return clean;
  } catch (e) {
    // Never let the sanitiser be the reason an event is malformed; sending
    // nothing is safer than sending something unscrubbed.
    return {};
  }
};

/** Resolves when the browser has a spare moment, or after 3s, whichever first. */
const whenIdle = () => {
  return new Promise((resolve) => {
    try {
      if (typeof window === 'undefined') {
        // No browser, so no first paint to stay out of the way of. This is the
        // test environment; waiting three seconds there buys nothing.
        resolve();
        return;
      }
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: 3000 });
        return;
      }
    } catch (e) { /* fall through to the timer */ }
    setTimeout(resolve, 3000);
  });
};

/**
 * Starts analytics if it is configured. Safe to call more than once, safe when
 * switched off, and never throws.
 */
export const startAnalytics = () => {
  if (posthogPromise) {
    return posthogPromise;
  }
  if (!isEnabled()) {
    posthogPromise = Promise.resolve(null);
    return posthogPromise;
  }

  const values = config();

  // The SDK is 98KB gzipped - more than the portal's own first load - and
  // nothing it records is needed in the first seconds. Fetching it once the
  // browser is idle keeps it out of the way of the page the user is waiting
  // for. Faro is not deferred this way: it has to be in place early enough to
  // catch a startup error.
  posthogPromise = whenIdle()
    .then(() => import(/* webpackChunkName: "analytics" */ 'posthog-js'))
    .then((module) => {
      const sdk = module.default || module;
      sdk.init(values.key, {
        api_host: values.host,

        // Nothing is captured that we did not ask for. Autocapture records
        // every click and the text around it, which on this portal means
        // driver names, plate numbers and company details; session recording
        // is the same problem with pictures. Both stay off.
        autocapture: false,
        disable_session_recording: true,
        capture_pageview: false,
        capture_pageleave: true,

        // Nobody is identified, so PostHog holds no person record tying these
        // events to a named driver or manager.
        person_profiles: 'identified_only',

        sanitize_properties: sanitize,

        // sanitize_properties can only rewrite an event; before_send can refuse
        // it. Browser noise is refused here rather than scrubbed.
        before_send: dropNoisyEvent,
        loaded: (instance) => {
          instance.register({
            app_version: values.version,
            environment: environment(values.api),
          });
        },
      });
      posthog = sdk;
      return sdk;
    })
    .catch(() => {
      // Blocked by an extension, offline, or a bad key. The portal carries on.
      posthogPromise = Promise.resolve(null);
      return null;
    });

  return posthogPromise;
};

/** The live instance once started, or null. Used by useRum(). */
export const analyticsInstance = () => posthog;

export const analyticsEnabled = isEnabled;

export const analyticsSanitize = sanitize;
